import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const ForceLineChart = ({ chartData }) => {
  const [hoveredSeries, setHoveredSeries] = useState(null);
  const [hasAnimated, setHasAnimated] = useState(false);

  // Color palette for different lines (similar to the image)
  const colors = [
    '#EF4444', // red
    '#8B5CF6', // purple
    '#3B82F6', // blue
    '#10B981', // green
    '#F59E0B', // orange
    '#EC4899', // pink
    '#6366F1', // indigo
    '#14B8A6', // teal
  ];

  // Transform the data structure for Recharts - MEMOIZED to prevent re-animation
  // We need to combine all series into a single array of objects
  // where each object represents one x-position with all y-values
  const transformedData = useMemo(() => {
    if (!chartData || !chartData.series || chartData.series.length === 0) {
      return [];
    }

    const data = [];
    const allXPositions = new Set();

    // Collect all unique x positions
    chartData.series.forEach(serie => {
      serie.data.forEach(point => {
        allXPositions.add(point.x);
      });
    });

    // Sort x positions
    const sortedXPositions = Array.from(allXPositions).sort((a, b) => a - b);

    // Create data points for each x position
    sortedXPositions.forEach(xPos => {
      const dataPoint = { position: xPos };

      chartData.series.forEach(serie => {
        const point = serie.data.find(p => p.x === xPos);
        if (point) {
          dataPoint[serie.name] = point.y;
        }
      });

      data.push(dataPoint);
    });

    return data;
  }, [chartData]);

  // Disable animation after first render
  useEffect(() => {
    const timer = setTimeout(() => setHasAnimated(true), 1000);
    return () => clearTimeout(timer);
  }, []);

  // Custom Legend with hover effect - MEMOIZED to prevent re-renders
  const renderLegend = useCallback((props) => {
    const { payload } = props;
    return (
      <div
        className="flex flex-wrap justify-center gap-4 pt-4"
        onMouseLeave={() => setHoveredSeries(null)}
      >
        {payload.map((entry, index) => (
          <div
            key={`legend-${index}`}
            className="flex items-center gap-2 cursor-pointer hover:opacity-80 transition-opacity"
            onMouseOver={() => setHoveredSeries(entry.value)}
            style={{
              opacity: hoveredSeries === null || hoveredSeries === entry.value ? 1 : 0.4,
              fontWeight: hoveredSeries === entry.value ? 'bold' : 'normal'
            }}
          >
            <div
              style={{
                width: '20px',
                height: '3px',
                backgroundColor: entry.color,
                borderRadius: '2px'
              }}
            />
            <span className="text-sm text-gray-700">{entry.value}</span>
          </div>
        ))}
      </div>
    );
  }, [hoveredSeries]);

  // Early return AFTER all hooks
  if (!chartData || !chartData.series || chartData.series.length === 0) {
    return (
      <div className="text-gray-500 text-sm italic p-4">
        No chart data available
      </div>
    );
  }

  return (
    <div className="w-full h-full bg-white rounded-lg p-4 border border-gray-200 flex flex-col">
      <h4 className="text-sm font-semibold text-gray-700 mb-3">
        Force Curve over Positions (Line Chart)
      </h4>
      <div className="flex-1 min-h-0">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart
            data={transformedData}
            margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
            <XAxis
              dataKey="position"
              label={{ value: 'Position', position: 'insideBottom', offset: -5 }}
              stroke="#6B7280"
            />
            <YAxis
              label={{ value: 'Force (kN)', angle: -90, position: 'insideLeft' }}
              stroke="#6B7280"
            />
            <Tooltip
              contentStyle={{
                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                border: '1px solid #D1D5DB',
                borderRadius: '8px'
              }}
            />
            <Legend content={renderLegend} />
            {chartData.series.map((serie, index) => {
              const isHovered = hoveredSeries === serie.name;
              const isOtherHovered = hoveredSeries !== null && hoveredSeries !== serie.name;

              return (
                <Line
                  key={serie.name}
                  type="monotone"
                  dataKey={serie.name}
                  stroke={colors[index % colors.length]}
                  strokeWidth={isHovered ? 4 : 2}
                  strokeOpacity={isOtherHovered ? 0.2 : 1}
                  dot={{ r: isHovered ? 5 : 4, opacity: isOtherHovered ? 0.2 : 1 }}
                  activeDot={{ r: isHovered ? 7 : 6 }}
                  isAnimationActive={!hasAnimated}
                  animationDuration={800}
                  onMouseEnter={() => setHoveredSeries(serie.name)}
                  onMouseLeave={() => setHoveredSeries(null)}
                />
              );
            })}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default React.memo(ForceLineChart);
